export enum UserRole {
  OWNER = 'OWNER',
  PROVIDER = 'PROVIDER',
  STAFF = 'STAFF',
}
export enum PLANCODES {
  
}

export enum BillingCycle {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export enum PlanFeature {
  DOWNPAYMENTS = 'DOWNPAYMENTS',
  BOOKING_SUBDOMAIN = 'BOOKING_SUBDOMAIN',
  SMS_NOTIFICATIONS = 'SMS_NOTIFICATIONS',
  EMAIL_NOTIFICATIONS = 'EMAIL_NOTIFICATIONS',
  MARKETING_CAMPAIGNS = 'MARKETING_CAMPAIGNS',
  ADVANCED_ANALYTICS = 'ADVANCED_ANALYTICS',
  MULTI_STAFF = 'MULTI_STAFF',
}

export enum PaymentProcessor {
  STRIPE = 'STRIPE',
  SQUARE = 'SQUARE',
}

export enum DepositType {
  PERCENTAGE = 'PERCENTAGE',
  FLAT = 'FLAT',
}

/** Per-service deposit override (Premium feature). Overrides the business-level deposit setting for this service. */
export interface ServiceDepositOverride {
  depositDisabled: boolean;
  depositType?: DepositType | null;
  depositAmount: number;
}
export enum ContactMethod {
  SMS = 'SMS',
  EMAIL = 'EMAIL',
}

export enum CustomerContactPreference {
  NONE = 'NONE',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  BOTH = 'BOTH',
}

export enum BookingAdvanceUnit {
  DAYS = 'DAYS',
  WEEKS = 'WEEKS',
  MONTHS = 'MONTHS',
}

export enum AppointmentStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  NO_SHOW = 'NO_SHOW',
  COMPLETED = 'COMPLETED',
}

export enum AppointmentCreator {
  CLIENT = 'CLIENT',
  STAFF = 'STAFF',
}

export enum ServiceStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
  PENDING_DELETION = 'PENDING_DELETION', // For "delete after appointments"
}

export enum PaymentStatus {
  UNPAID = 'UNPAID',
  DEPOSIT_PAID = 'DEPOSIT_PAID',
  PAID = 'PAID',
}

export enum PaymentCollectionStatus {
  NOT_REQUIRED = 'NOT_REQUIRED',
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
}

export enum DepositRefundStatus {
  PENDING = 'PENDING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
}

export enum MessageSender {
  CLIENT = 'CLIENT',
  PROVIDER = 'PROVIDER',
}

export enum DayOfWeek {
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
}

export enum OnboardingStatus {
  NEEDS_BUSINESS_SETUP = 'NEEDS_BUSINESS_SETUP',
  NEEDS_STYLE_SETUP = 'NEEDS_STYLE_SETUP',
  NEEDS_TOS_ACCEPTANCE = 'NEEDS_TOS_ACCEPTANCE',
  NEEDS_SUBSCRIPTION = 'NEEDS_SUBSCRIPTION',
  COMPLETE = 'COMPLETE',
}

export interface TimeSlot {
  start: string;
  end: string;
}

export interface Contact {
  phone: string;
  email: string;
  address: string;
}

export interface Plan {
  planId: string;
  billingCycle: BillingCycle;
  features: PlanFeature[];
}

export interface WorkingHours {
  Monday: TimeSlot[];
  Tuesday: TimeSlot[];
  Wednesday: TimeSlot[];
  Thursday: TimeSlot[];
  Friday: TimeSlot[];
  Saturday: TimeSlot[];
  Sunday: TimeSlot[];
}

export interface Breaks {
  Monday: TimeSlot[];
  Tuesday: TimeSlot[];
  Wednesday: TimeSlot[];
  Thursday: TimeSlot[];
  Friday: TimeSlot[];
  Saturday: TimeSlot[];
  Sunday: TimeSlot[];
}

export interface TimeOffPeriod {
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  label?: string;
}

export interface PaymentSettings {
  paymentProcessor: PaymentProcessor;
  currency: string;
  requireDeposit: boolean;
  depositType: DepositType;
  depositAmount: number;
  isRefundable: boolean;
  refundWindowHours: number;
}

export interface PaymentSetupSummary {
  stripeConnectedAccountId?: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  depositsReady: boolean;
  onboardingUrl?: string | null;
}

export interface AppointmentPaymentDetails {
  depositRequired: boolean;
  depositCollectionStatus: PaymentCollectionStatus;
  depositPaymentIntentId?: string | null;
  stripeConnectedAccountId?: string | null;
  depositClientSecret?: string | null;
  depositPaymentExpiresAt?: string | null;
  depositAmount: number;
  totalAmount: number;
  currency: string;
  isRefundable: boolean;
  refundWindowHours: number;
  depositRefundStatus?: DepositRefundStatus | null;
  depositRefundId?: string | null;
  depositRefundedAt?: string | null;
}

export interface NotificationSettings {
  sendEmailReminders: boolean;
  sendSmsReminders: boolean;
  reminderOffsetsMinutes: number[];
  sendBookingConfirmation: boolean;
  sendCancellationNotice: boolean;
  customerContactPreference: CustomerContactPreference;
  customerContactEmail?: string | null;
  customerContactPhone?: string | null;
}

export interface BookingSettings {
  advanceBookingValue: number;
  advanceBookingUnit: BookingAdvanceUnit;
  requireClientConfirmation: boolean;
  sendConfirmationEmail: boolean;
  appointmentInstructions?: string | null;
}

export interface Business {
  id: string;
  ownerUserId: string;
  name: string;
  bookingSlug?: string;
  contact: Contact;
  logoUrl?: string;
  timezone: string;
  plan: Plan;
  workingHours?: WorkingHours;
  breaks?: Breaks;
  timeOff?: TimeOffPeriod[];
  paymentSettings: PaymentSettings;
  notificationSettings: NotificationSettings;
  bookingSettings?: BookingSettings;
  createdAt: string;
  updatedAt: string;
}

export interface Branding {
  colorScheme?: object;
  colorSchemeName?: string;
  fontTheme?: object;
  fontThemeName?: string;
  logoUrl?: string;
  heroImageUrl?: string;
  headerStyle?: string;
}

export interface User {
  id: string;
  businessId: string;
  email: string;
  name: string;
  role: UserRole;
  isActive: boolean;
  bookingSlug: string;
  branding?: Branding;
  onboardingStatus?: OnboardingStatus;
  createdAt: string;
  updatedAt?: string;
}

export interface AccountDeletionState {
  deactivated: boolean;
  deactivatedAt?: string | null;
  scheduledDeletionAt?: string | null;
}

export interface Client {
  id: string;
  businessId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferredContactMethod: ContactMethod;
  allowEmail: boolean;
  allowSms: boolean;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface Service {
  id: string;
  businessId: string;
  name: string;
  description: string;
  durationMinutes: number;
  price: number;
  currency: string;
  isActive: boolean;
  status?: ServiceStatus;
  scheduledDeletionDate?: string;
  depositOverride?: ServiceDepositOverride | null;
}

export interface ProviderService {
  providerId: string;
  serviceId: string;
  priceOverride?: number;
  durationOverrideMinutes?: number;
  isActive: boolean;
}

export interface ProviderProfile {
  id: string;
  businessId: string;
  userId?: string;
  name: string;
  email?: string;
  role: UserRole;
  isActive: boolean;
  workingHours: WorkingHours;
  breaks: Breaks;
  timeOff: TimeOffPeriod[];
  createdAt: string;
  updatedAt: string;
}

export interface Appointment {
  id: string;
  businessId: string;
  clientId: string;
  serviceId: string;
  serviceName: string;
  providerId: string;
  start: string;
  end: string;
  status: AppointmentStatus;
  notes: string;
  createdBy: AppointmentCreator;
  paymentStatus: PaymentStatus;
  paymentDetails?: AppointmentPaymentDetails | null;
  createdAt: string;
  updatedAt: string;
}
